import { Client } from '../types/client';

/**
 * Agencies: a client attached to a root client (`parent_id`). The tree is
 * small and comes whole with the client list, so these helpers work on an
 * id → client map built by the caller.
 */
export type ClientIndex = Map<string, Client>;

export const indexClients = (clients: Client[]): ClientIndex => new Map(clients.map((c) => [c.id, c]));

/** The root of a client's group: its parent when known, itself otherwise. */
export const rootOf = (client: Client, byId: ClientIndex): Client =>
  (client.parent_id && byId.get(client.parent_id)) || client;

/** Whether this client is an agency: it names a parent, known or not. */
export const isAgency = (client: Client): boolean => Boolean(client.parent_id) || client.isAgency === true;

/** "A2A › A2A Lyon" for an agency whose root is known, the plain name otherwise. */
export const clientPathLabel = (client: Client, byId: ClientIndex): string => {
  const parent = client.parent_id ? byId.get(client.parent_id) : undefined;
  return parent ? `${parent.name} › ${client.name}` : client.name;
};

/** The agencies of a root, by name. */
export const agenciesOf = (rootId: string, clients: Client[]): Client[] =>
  clients.filter((c) => c.parent_id === rootId).sort((a, b) => a.name.localeCompare(b.name));

/** The agency's SIRET when its root's SIREN and its NIC are both known. */
export const siretOf = (client: Client, byId: ClientIndex): string | null => {
  if (!client.nic) return null;
  const root = rootOf(client, byId);
  return root.siren ? `${root.siren}${client.nic}` : null;
};
